import * as React from "react";
import Box from "@mui/material/Box";
import { Text, ITextStyles } from "@fluentui/react";
import { Icon } from "@fluentui/react/lib/Icon";
import { Checkbox, ICheckboxStyleProps, ICheckboxStyles } from "@fluentui/react";
import { LoadStatus, StatusHistoryEntry } from "@src/types/api";
import { formatNumber } from "@taskpane/utils/shipmentUtils";
import type { LocationDisplay } from "@taskpane/utils/formUtils";
import { CheckCircle, History, XCircle } from "lucide-react";
import { resolveShipmentStatus, resolveStatusHistoryEntry } from "@shared/status/statusResolver";
import { interactionLeadLocalPart } from "@taskpane/utils/interactionLeadDisplay";
import type { StatusDefinition } from "@shared/status/types";
import { StatusPill } from "./StatusPill";
import { strings } from "@src/i18n/strings";
import { colors } from "@taskpane/theme";

const VERIFIED_COLOR = "#3b82f6";
const NOT_VERIFIED_COLOR = "#ef4444";

function buildCheckboxStyles(props: ICheckboxStyleProps): Partial<ICheckboxStyles> {
  const base: Partial<ICheckboxStyles> = {
    checkmark: {
      opacity: "0 !important",
      color: "transparent !important",
    },
    checkbox: {
      width: 10,
      height: 10,
      backgroundColor: "transparent",
      borderColor: "rgb(122, 114, 114)",
      selectors: {
        ":hover": { backgroundColor: "#f5f7f9", borderColor: "rgb(122, 114, 114)" },
      },
    },
  };

  if (props.disabled) {
    return {
      ...base,
      checkbox: {
        ...(base.checkbox as ICheckboxStyles),
        borderColor: "rgb(200, 200, 200)",
        opacity: 0.5,
        selectors: {
          ":hover": { backgroundColor: "transparent", borderColor: "rgb(200, 200, 200)" },
        },
      },
    };
  }
  if (!props.checked) return base;
  return {
    ...base,
    checkmark: {
      opacity: "1 !important",
      color: "white !important",
    },
    checkbox: {
      ...(base.checkbox as ICheckboxStyles),
      backgroundColor: colors.primary,
      borderColor: "rgb(122, 114, 114)",
      selectors: {
        ":hover": { backgroundColor: colors.primaryHover, borderColor: "rgb(122, 114, 114)" },
      },
    },
  };
}

function formatStatusHistoryTime(value: string): string {
  const date = new Date(value);
  const rowStrings = strings.components.shipmentRow;

  if (Number.isNaN(date.getTime())) return value || rowStrings.defaults.unknownTime;
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

function statusHistoryUserDisplayLabel(
  interactingUser: string | undefined,
  unknownUserLabel: string
): string {
  const t = interactingUser?.trim() ?? "";

  if (!t) return unknownUserLabel;
  return interactionLeadLocalPart(t) ?? t;
}

function StatusHistoryPanel({
  history,
  statuses,
  classification,
  bolClassification,
}: {
  history?: StatusHistoryEntry[];
  statuses: StatusDefinition[];
  classification?: string | null;
  bolClassification?: string | null;
}) {
  const rowStrings = strings.components.shipmentRow;
  const entries = Array.isArray(history) ? history.filter((entry) => entry?.status) : [];

  return (
    <Box className="shipment-status-history-panel" sx={styles.statusHistoryPanel}>
      <Text styles={styles.statusHistoryTitle}>{rowStrings.defaults.statusHistoryTitle}</Text>
      {entries.length > 0 ? (
        <Box sx={styles.statusHistoryList}>
          {entries.map((entry, index) => {
            const entryResolved = resolveStatusHistoryEntry(
              entry.status,
              {
                classification,
                bolClassification,
              },
              statuses
            );

            return (
              <Box
                key={`${entry.status}-${entry.updateTime}-${index}`}
                className="shipment-status-history-row"
                sx={styles.statusHistoryRow}
              >
                <Box sx={styles.statusHistoryStatusCell}>
                  <StatusPill status={entryResolved} />
                </Box>
                <Text styles={styles.statusHistoryUserText}>
                  {statusHistoryUserDisplayLabel(
                    entry.interactingUser,
                    rowStrings.defaults.unknownUser
                  )}
                </Text>
                <Text styles={styles.statusHistoryTimeText}>
                  {formatStatusHistoryTime(entry.updateTime)}
                </Text>
              </Box>
            );
          })}
        </Box>
      ) : (
        <Text styles={styles.statusHistoryEmptyText}>
          {rowStrings.defaults.noStatusHistoryRecorded}
        </Text>
      )}
    </Box>
  );
}

export interface ShipmentRowProps {
  origin: LocationDisplay;
  destination: LocationDisplay;
  detailLeft: string;
  modeLabel: "LTL" | "FTL";
  weightLbs: number;
  isQuoted: boolean;
  replySelectionEnabled: boolean;
  priceText: string;
  loadStatus?: LoadStatus;
  statusHistory?: StatusHistoryEntry[];
  classification?: string | null;
  bolClassification?: string | null;
  statuses: StatusDefinition[];
  interactionLeadLabel?: string;
  verifiedSender?: boolean;
  selected: boolean;
  isLoading?: boolean;
  tmsLoadId?: string;
  onOpen: () => void;
  onToggleSelect: (checked: boolean) => void;
  onBook: () => void;
}
export default function ShipmentRow({
  origin,
  destination,
  detailLeft,
  modeLabel,
  weightLbs,
  isQuoted,
  replySelectionEnabled,
  priceText,
  loadStatus,
  statusHistory,
  classification,
  bolClassification,
  statuses,
  interactionLeadLabel,
  verifiedSender,
  selected,
  isLoading,
  tmsLoadId,
  onOpen,
  onToggleSelect,
  onBook,
}: ShipmentRowProps) {
  const rowStrings = strings.components.shipmentRow;
  const [historyOpen, setHistoryOpen] = React.useState(false);
  const resolvedStatus = resolveShipmentStatus(
    { loadStatus, classification, bolClassification },
    statuses
  );
  const metaText = `${detailLeft} \u2022 ${modeLabel} \u2022 ${formatNumber(weightLbs)} lbs`;
  const ftlShowingPricedRate =
    modeLabel === strings.shared.shipmentMode.ftl &&
    priceText !== rowStrings.defaults.noRateSelectedTitle &&
    priceText.trim() !== "";
  const offerLineHighlighted = isQuoted || ftlShowingPricedRate;
  const offerText =
    modeLabel === strings.shared.shipmentMode.ftl
      ? priceText === rowStrings.defaults.noRateSelectedTitle || !priceText.trim()
        ? rowStrings.defaults.noRateSelectedBody
        : priceText
      : isQuoted
        ? priceText
        : rowStrings.defaults.noRateSelectedBody;

  return (
    <Box
      className="shipment-item"
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
      sx={styles.shipmentItem}
    >
      <Box
        className="shipment-checkbox"
        onClick={(e) => e.stopPropagation()}
        title={rowStrings.includeInReply}
        sx={styles.checkboxContainer}
      >
        <Checkbox
          styles={buildCheckboxStyles}
          ariaLabel={rowStrings.includeInReply}
          checked={selected}
          onChange={(_, checked) => {
            if (replySelectionEnabled) {
              onToggleSelect(Boolean(checked));
            }
          }}
          disabled={!replySelectionEnabled}
        />
      </Box>

      <Box className="shipment-content" sx={styles.content}>
        <Box className="shipment-route-row" sx={styles.routeRow}>
          <Box className="shipment-route" sx={styles.routeLine}>
            <Text styles={styles.cityText}>{origin.line1}</Text>
            <Icon iconName="Forward" styles={{ root: styles.arrowIcon }} />
            <Text styles={styles.cityText}>{destination.line1}</Text>
          </Box>
          {interactionLeadLabel ? (
            <Box
              className="shipment-interaction-lead-pill"
              sx={styles.interactionLeadPill}
              aria-label={rowStrings.aria.activeOnThread(interactionLeadLabel)}
            >
              <Box component="span" sx={styles.interactionLeadName}>
                {interactionLeadLabel}
              </Box>
              {typeof verifiedSender === "boolean" ? (
                <Box
                  component="span"
                  sx={styles.interactionLeadVerifyIcon}
                  title={
                    verifiedSender ? rowStrings.aria.senderVerified : rowStrings.aria.senderNotFound
                  }
                >
                  {verifiedSender ? (
                    <CheckCircle
                      size={16}
                      color={VERIFIED_COLOR}
                      aria-label={rowStrings.aria.senderVerified}
                    />
                  ) : (
                    <XCircle
                      size={16}
                      color={NOT_VERIFIED_COLOR}
                      aria-label={rowStrings.aria.senderNotFound}
                    />
                  )}
                </Box>
              ) : null}
            </Box>
          ) : null}
        </Box>

        {(origin.line2 || destination.line2) && (
          <Box className="shipment-zips" sx={styles.zipLine}>
            <Text styles={styles.secondaryText}>
              {origin.line2 || rowStrings.defaults.unknownLocation} {"\u2192"}{" "}
              {destination.line2 || rowStrings.defaults.unknownLocation}
            </Text>
          </Box>
        )}

        <Box className="shipment-meta" sx={styles.metaLine}>
          <Text styles={styles.secondaryText}>{metaText}</Text>
        </Box>

        {tmsLoadId && (
          <Box className="shipment-tms-load-id" sx={styles.tmsLoadIdLine}>
            <Text styles={styles.tmsLoadIdText}>
              {rowStrings.defaults.tmsPrefix}
              {tmsLoadId}
            </Text>
          </Box>
        )}

        <Box className="shipment-bottom-row" sx={styles.bottomRow}>
          <Text styles={offerLineHighlighted ? styles.offerText : styles.noRateText}>
            {offerText}
          </Text>
          <Box onClick={(e) => e.stopPropagation()} sx={styles.statusActions}>
            <Box
              component="button"
              type="button"
              className="shipment-status-history-button"
              aria-label={rowStrings.defaults.showStatusHistory}
              aria-expanded={historyOpen}
              onClick={() => setHistoryOpen((open) => !open)}
              sx={styles.statusHistoryButton}
            >
              <History size={15} aria-hidden="true" />
            </Box>
            <StatusPill status={resolvedStatus} isLoading={isLoading} />
            {resolvedStatus.showBook && !isLoading ? (
              <Box
                component="button"
                type="button"
                className="shipment-book-button"
                onClick={onBook}
                sx={styles.bookButton}
              >
                {rowStrings.defaults.book}
              </Box>
            ) : null}
          </Box>
        </Box>
        {historyOpen ? (
          <Box onClick={(e) => e.stopPropagation()}>
            <StatusHistoryPanel
              history={statusHistory}
              statuses={statuses}
              classification={classification}
              bolClassification={bolClassification}
            />
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}

const styles = {
  shipmentItem: {
    display: "flex",
    alignItems: "flex-start",
    gap: "6px",
    padding: "8px 4px",
    cursor: "pointer",
    borderBottom: "1px solid #edebe9",
    "&:hover": { backgroundColor: "#faf9f8" },
  },
  checkboxContainer: {
    paddingTop: "2px",
    flexShrink: 0,
  },
  content: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
  routeRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "8px",
    minWidth: 0,
  },
  routeLine: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    minWidth: 0,
    flex: 1,
  },
  interactionLeadPill: {
    borderRadius: 999,
    padding: "3px 10px",
    fontSize: "x-small",
    fontWeight: 600,
    lineHeight: 1.4,
    whiteSpace: "nowrap",
    textAlign: "left",
    display: "inline-flex",
    flexDirection: "row",
    alignItems: "center",
    gap: "6px",
    flexShrink: 0,
    maxWidth: "42%",
    minWidth: 0,
    overflow: "hidden",
    background: "#e8e8e8",
    color: "#605e5c",
    border: "1px solid #d2d0ce",
  },
  interactionLeadName: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    minWidth: 0,
  },
  interactionLeadVerifyIcon: {
    display: "inline-flex",
    alignItems: "center",
    flexShrink: 0,
    lineHeight: 0,
  },
  cityText: {
    root: {
      fontWeight: 600,
      fontSize: "13px",
      lineHeight: "18px",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
  } as ITextStyles,
  arrowIcon: {
    fontSize: "10px",
    color: "#a19f9d",
    flexShrink: 0,
  },
  zipLine: {
    display: "flex",
    alignItems: "center",
  },
  metaLine: {
    marginTop: "2px",
  },
  tmsLoadIdLine: {
    marginTop: "2px",
  },
  tmsLoadIdText: {
    root: {
      fontSize: "10px",
      color: colors.primary,
      lineHeight: "14px",
      fontWeight: 500,
    },
  } as ITextStyles,
  secondaryText: {
    root: {
      fontSize: "11px",
      color: "#605e5c",
      lineHeight: "16px",
    },
  } as ITextStyles,
  bottomRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: "4px",
    gap: "8px",
  },
  offerText: {
    root: {
      fontSize: "12px",
      fontWeight: 600,
      color: "#323130",
      lineHeight: "16px",
    },
  } as ITextStyles,
  noRateText: {
    root: {
      fontSize: "11px",
      color: colors.textMuted,
      lineHeight: "16px",
    },
  } as ITextStyles,
  statusActions: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    flexShrink: 0,
    marginLeft: "8px",
  },
  statusHistoryButton: {
    width: 26,
    height: 26,
    border: "1px solid #d2d0ce",
    borderRadius: "50%",
    background: "#ffffff",
    color: "#605e5c",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    cursor: "pointer",
    lineHeight: 0,
    "&:hover": {
      background: "#f5f7f9",
      color: "#323130",
      borderColor: "#a19f9d",
    },
    "&:focus-visible": {
      outline: "2px solid #0f6cbd",
      outlineOffset: 2,
    },
  },
  bookButton: {
    height: 24,
    border: "1px solid #d2d0ce",
    borderRadius: "999px",
    background: "#ffffff",
    color: "#323130",
    padding: "0 9px",
    fontSize: "11px",
    fontWeight: 600,
    cursor: "pointer",
    lineHeight: "22px",
    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.04)",
    "&:hover": {
      background: "#f5f7f9",
      borderColor: "#a19f9d",
      color: "#201f1e",
    },
    "&:focus-visible": {
      outline: "2px solid #0f6cbd",
      outlineOffset: 2,
    },
  },
  statusHistoryPanel: {
    marginTop: "8px",
    padding: "10px",
    border: "1px solid #edebe9",
    borderRadius: "6px",
    background: "#ffffff",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
    maxHeight: "260px",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  statusHistoryTitle: {
    root: {
      display: "block",
      marginBottom: "8px",
      fontSize: "11px",
      fontWeight: 700,
      color: "#323130",
      lineHeight: "14px",
    },
  } as ITextStyles,
  statusHistoryList: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    overflowY: "auto",
    paddingRight: "4px",
  },
  statusHistoryRow: {
    display: "grid",
    gridTemplateColumns: "minmax(92px, max-content) minmax(0, 1fr)",
    gap: "2px 8px",
    alignItems: "center",
  },
  statusHistoryStatusCell: {
    gridRow: "1 / span 2",
  },
  statusHistoryUserText: {
    root: {
      fontSize: "11px",
      fontWeight: 600,
      color: "#323130",
      lineHeight: "14px",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
  } as ITextStyles,
  statusHistoryTimeText: {
    root: {
      fontSize: "10px",
      color: "#605e5c",
      lineHeight: "14px",
    },
  } as ITextStyles,
  statusHistoryEmptyText: {
    root: {
      fontSize: "11px",
      color: "#605e5c",
      lineHeight: "16px",
      fontStyle: "italic",
    },
  } as ITextStyles,
};
